import { useEffect, useMemo, useRef, useState } from 'react'
import { LiveAttributionByLive, LiveAttributionWindow } from '../../types'
import { renderLiveReportHtml } from './live-report-html'
import {
  buildLiveReportModel,
  defaultSelection,
  includedSections,
  LIVE_REPORT_BLOCKS,
  LIVE_REPORT_SECTION_ORDER,
  LIVE_REPORT_SECTION_TITLES,
  LiveReportBlockId,
  LiveReportSectionId,
  LiveReportSelection,
} from './live-report-model'

/**
 * Pré-visualização e download do relatório de live.
 *
 * A pré-visualização É o arquivo: o mesmo HTML que vai ser baixado, mostrado
 * num iframe. "Baixar HTML" salva essa página; "Baixar PDF" abre a impressão
 * do navegador sobre ela (o CSS de impressão já ajusta para A4 retrato) — é
 * só escolher "Salvar como PDF". Não existe a hipótese de um dos arquivos sair
 * diferente do que a pessoa viu.
 *
 * À esquerda, as seções na ordem da página, com os blocos de cada uma
 * indentados. Seção ou bloco sem dado nesta execução aparece desligado, com o
 * motivo, em vez de imprimir um espaço vazio.
 */
export function LiveReportPreviewModal({ live, companyTitle, window, onClose }: {
  live: LiveAttributionByLive
  companyTitle: string
  window: LiveAttributionWindow
  onClose: () => void
}) {
  const model = useMemo(() => buildLiveReportModel({ live, companyTitle, window }), [live, companyTitle, window])
  const [selection, setSelection] = useState<LiveReportSelection>(() => defaultSelection(model))
  const html = useMemo(() => renderLiveReportHtml(model, selection), [model, selection])
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [printing, setPrinting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onClose])

  const included = includedSections(selection, model)
  const fileBase = [
    'resultados-da-live',
    slug(companyTitle),
    model.liveTitle ? slug(model.liveTitle) : '',
    model.fileDate,
  ].filter(Boolean).join('-')

  const toggleSection = (id: LiveReportSectionId) =>
    setSelection((prev) => ({ ...prev, sections: { ...prev.sections, [id]: !prev.sections[id] } }))
  const toggleBlock = (id: LiveReportBlockId) =>
    setSelection((prev) => ({ ...prev, blocks: { ...prev.blocks, [id]: !prev.blocks[id] } }))
  const setAll = (value: boolean) =>
    setSelection(() => {
      const base = defaultSelection(model)
      if (value) return base
      const sections = { ...base.sections }
      for (const id of LIVE_REPORT_SECTION_ORDER) sections[id] = false
      return { ...base, sections }
    })

  const downloadHtml = () => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `${fileBase}.html`
    a.click()
    // O clique já copiou a URL para o download; revogar em seguida libera o blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const printPdf = async () => {
    const frame = frameRef.current?.contentWindow
    if (!frame) return
    setPrinting(true)
    try {
      // Sem esperar as fontes, a impressão sai com a fonte de fallback.
      await frame.document.fonts?.ready
      frame.focus()
      frame.print()
    } finally {
      setPrinting(false)
    }
  }

  const disabled = included.length === 0

  return (
    <div className="modal-overlay active" onClick={onClose} role="presentation">
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-label="Relatório da live"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 1360, width: 'calc(100vw - 40px)' }}
      >
        <div className="modal-header">
          <div>
            <h2 style={{ margin: 0, fontSize: 17 }}>Relatório da live</h2>
            <div style={{ fontSize: 12, color: 'var(--ink-500, #6b7280)', marginTop: 2 }}>
              {companyTitle}{model.liveTitle ? ` · ${model.liveTitle}` : ''} · {model.hero.meta.join(' · ')}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', minHeight: 520 }}>
          {/* Seleção de seções e blocos */}
          <aside style={{ borderRight: '1px solid var(--ink-200, #e5e8ee)', padding: '14px 16px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
                Seções
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-400, #9aa1ad)' }}>
                <button type="button" onClick={() => setAll(true)} style={linkBtn}>todas</button>
                {' · '}
                <button type="button" onClick={() => setAll(false)} style={linkBtn}>nenhuma</button>
              </span>
            </div>

            {LIVE_REPORT_SECTION_ORDER.map((id) => {
              const avail = model.availability[id]
              const checked = selection.sections[id] && avail.available
              const blocks = LIVE_REPORT_BLOCKS.filter((b) => b.section === id)
              return (
                <div key={id} style={{ marginBottom: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: avail.available ? 'pointer' : 'default', opacity: avail.available ? 1 : 0.55 }}>
                    <input type="checkbox" checked={checked} disabled={!avail.available} onChange={() => toggleSection(id)} style={{ marginTop: 3 }} />
                    <span style={{ fontSize: 12, color: 'var(--ink-800, #1a1d23)', lineHeight: 1.4 }}>
                      {LIVE_REPORT_SECTION_TITLES[id]}
                      {!avail.available && avail.reason && (
                        <div style={{ fontSize: 10, color: 'var(--ink-400, #9aa1ad)' }}>{avail.reason}</div>
                      )}
                    </span>
                  </label>
                  {blocks.length > 0 && checked && (
                    <div style={{ marginLeft: 24, marginTop: 2 }}>
                      {blocks.map((b) => {
                        const bAvail = model.blockAvailability[b.id]
                        return (
                          <label key={b.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: bAvail.available ? 'pointer' : 'default', padding: '2px 0', opacity: bAvail.available ? 1 : 0.55 }}>
                            <input type="checkbox" checked={selection.blocks[b.id] && bAvail.available} disabled={!bAvail.available} onChange={() => toggleBlock(b.id)} style={{ marginTop: 2 }} />
                            <span style={{ fontSize: 11, color: 'var(--ink-600, #4a5160)', lineHeight: 1.35 }}>
                              {b.label}
                              {!bAvail.available && bAvail.reason && (
                                <div style={{ fontSize: 10, color: 'var(--ink-400, #9aa1ad)' }}>{bAvail.reason}</div>
                              )}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </aside>

          {/* Prévia = o próprio arquivo */}
          <div style={{ padding: 16, background: 'var(--ink-100, #f1f3f7)', display: 'flex', flexDirection: 'column' }}>
            {disabled ? (
              <div style={{ ...emptyMsg }}>Nenhuma seção selecionada. Marque ao menos uma à esquerda.</div>
            ) : (
              <iframe
                ref={frameRef}
                title="Prévia do relatório"
                // Sem `allow-scripts`: o relatório é HTML/CSS puro, e nada que
                // venha de dado (título de live, nome de produto) pode executar
                // aqui mesmo se escapar do `esc()`. `allow-same-origin` deixa o
                // painel chamar a impressão; `allow-modals` é o que libera a
                // janela de impressão num iframe com sandbox.
                sandbox="allow-same-origin allow-modals"
                srcDoc={html}
                style={{ width: '100%', flex: 1, minHeight: 'calc(100vh - 260px)', border: '1px solid var(--ink-200, #e5e8ee)', borderRadius: 8, background: '#F5F5F5' }}
              />
            )}
            <div style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)', marginTop: 8 }}>
              A prévia é o arquivo final. No PDF, escolha "Salvar como PDF" na janela de impressão.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderTop: '1px solid var(--ink-200, #e5e8ee)' }}>
          <span style={{ fontSize: 12, color: 'var(--ink-500, #6b7280)' }}>
            {included.length} de {LIVE_REPORT_SECTION_ORDER.length} seções no arquivo
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} className="btn" style={{ fontSize: 13 }}>Fechar</button>
            <button type="button" onClick={downloadHtml} disabled={disabled} style={actionBtn(!disabled, false)}>
              Baixar HTML
            </button>
            <button type="button" onClick={printPdf} disabled={disabled || printing} style={actionBtn(!disabled && !printing, true)}>
              {printing ? 'Preparando…' : 'Baixar PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--ink-500, #6b7280)', textDecoration: 'underline',
}

const emptyMsg: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: 'var(--ink-500, #6b7280)', fontSize: 13, padding: 24, textAlign: 'center',
}

function actionBtn(enabled: boolean, primary: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: enabled ? 'pointer' : 'not-allowed',
    border: primary ? 'none' : '1px solid var(--ink-200, #e5e8ee)',
    background: !enabled ? 'var(--ink-200, #e5e8ee)' : primary ? 'var(--accent-coral, #ff7a59)' : '#fff',
    color: !enabled ? 'var(--ink-400, #9aa1ad)' : primary ? '#fff' : 'var(--ink-800, #1a1d23)',
  }
}

function slug(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}
